// Shell-wide "currently selected project" state. Every CRM screen reads this
// to scope its lists, and writes (create lead, log call, etc) stamp it onto
// new records. Persisted to localStorage so the choice survives reloads.
//
// A project is always selected once projects have loaded — if none is stored,
// or the stored one no longer exists, we fall back to the first available.

import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useProjects } from "../hooks/useProjects";
import { useSelectedCompany } from "./SelectedCompanyProvider";

const STORAGE_KEY = "crm.selectedProjectId";

const SelectedProjectContext = createContext({
  projectId: null,
  setProjectId: () => {},
  project: null,
  projects: [],
  isLoading: false,
});

export function SelectedProjectProvider({ children }) {
  // Resolving = "we haven't yet confirmed what the user's project list is".
  // We treat the first fetch and any in-flight retry as resolving so the
  // welcome empty-state never flashes during a fresh-login race (where the
  // backend's CrmMembership gate may 403 the first attempt while it
  // bootstraps the membership row, then succeed on retry — see useProjects).
  const { companyQueryParams, isLoading: companyLoading } = useSelectedCompany();
  const { projects, byId, isLoading, isFetched, isFetching, isError } = useProjects();
  const [projectId, setProjectIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const setProjectId = (id) => {
    setProjectIdState(id || null);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  };

  // Ensure a valid project is always selected once projects have loaded.
  // If nothing is stored, or the stored id no longer exists, fall back to the
  // first available project.
  useEffect(() => {
    if (!isFetched || isFetching || projects.length === 0) return;
    if (!projectId || !byId.get(String(projectId))) {
      setProjectId(String(projects[0]._id));
    }
  }, [projectId, projects, byId, isFetched, isFetching, companyQueryParams]);

  const isResolving = isLoading || !isFetched || (isFetching && !isError) || companyLoading;

  const value = useMemo(() => ({
    projectId,
    setProjectId,
    project: projectId ? byId.get(String(projectId)) || null : null,
    projects,
    isLoading: isResolving,
  }), [projectId, byId, projects, isResolving]);

  return (
    <SelectedProjectContext.Provider value={value}>
      {children}
    </SelectedProjectContext.Provider>
  );
}

export function useSelectedProject() {
  return useContext(SelectedProjectContext);
}
